import * as Sentry from '@sentry/react';

import { fetchFeatureFlags } from '../hooks/useFeatureFlags';
import {
  getMongoBackendUrls,
  isBackendOverrideDisabled,
  isMongoBackendConfigured,
} from './backendOverride';

// Decides, ONCE per session (login / first boot), which backend the session talks to.
// The result is stored in redux (`state.user.backendTarget`) and read by
// utils/backendOverride.js on every request. This module never throws and
// never returns anything but 'env' | 'mongo'.
//
// Gates, in order (each one falls back to 'env'):
//   1. kill-switch on, or Mongo URLs not configured  -> 'env', no requests.
//   2. FRESH feature flags (forced, the module cache leaks across a logout
//      without reload) raced against timeoutMs; `mongoBackend` must be
//      strictly `true`.
//   3. Probes before switch, in parallel — REST and GraphQL switch together,
//      so BOTH hosts must confirm:
//        - REST: ONE authenticated GET <mongoRest>/feature-flags. Only an HTTP
//          200 whose body says `mongoBackend: true` confirms the Mongo backend
//          is up, accepts this token AND reads the same flag (that endpoint
//          answers 200 with everything false when its own flag read failed —
//          a 200 alone is not health).
//        - GraphQL: ONE authenticated POST <mongoGraphql> `{ __typename }`.
//          Only an HTTP 200 with `data.__typename` and no `errors` confirms.
//      Anything else is reported to Sentry (`backend_target.probe_failed`,
//      `reason` + `probe` tags only — no URLs, no tokens).
//
// timeoutMs is ONE budget for the whole resolution: the probe only gets what
// the flag fetch left, so a slow /feature-flags cannot stretch login to 2x.
//
// The probes deliberately do NOT go through the main axios / apollo clients:
// those resolve their host from the store, which still says 'env' while we
// are deciding. They use explicit absolute URLs instead.

export const BACKEND_TARGET_ENV = 'env';
export const BACKEND_TARGET_MONGO = 'mongo';
export const DEFAULT_RESOLVE_TIMEOUT_MS = 3000;
export const PROBE_PATH = '/feature-flags';
export const GRAPHQL_PROBE_QUERY = '{ __typename }';

const PROBE_REST = 'rest';
const PROBE_GRAPHQL = 'graphql';
const PROBE_SKIPPED = 'skipped';

const TIMEOUT_CODE = 'BACKEND_TARGET_TIMEOUT';

function readToken() {
  try {
    return localStorage.getItem('token') || '';
  } catch (e) {
    return '';
  }
}

function safeNow(now) {
  try {
    const value = now();
    return Number.isFinite(value) ? value : 0;
  } catch (e) {
    return 0;
  }
}

function normalizeTimeout(timeoutMs) {
  return Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : DEFAULT_RESOLVE_TIMEOUT_MS;
}

function createAbortController() {
  return typeof AbortController === 'function' ? new AbortController() : null;
}

// Runs `task({ signal })` and rejects with a TIMEOUT_CODE error if it has not
// settled after timeoutMs. The timer is always cleared; the signal is aborted
// on timeout so a real fetch is cancelled instead of left dangling.
function withTimeout(task, timeoutMs) {
  const controller = createAbortController();
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      if (controller) controller.abort();
      const error = new Error('backend target resolution timed out');
      error.code = TIMEOUT_CODE;
      reject(error);
    }, timeoutMs);
  });
  const run = Promise.resolve().then(() => task({ signal: controller ? controller.signal : undefined }));
  return Promise.race([run, timeout]).finally(() => clearTimeout(timer));
}

function isTimeoutError(error) {
  return Boolean(error) && (error.code === TIMEOUT_CODE || error.name === 'AbortError');
}

function probeFailureReason(error, result) {
  if (error) return isTimeoutError(error) ? 'timeout' : 'network';
  const status = result && typeof result === 'object' ? result.status : undefined;
  if (!Number.isInteger(status)) return 'network';
  if (status !== 200) return `http_${status}`;
  // 200 but the body is not the healthy answer the probe expects.
  return 'degraded';
}

function okBody(result) {
  if (!result || typeof result !== 'object' || result.status !== 200) return null;
  const body = result.body;
  return Boolean(body) && typeof body === 'object' && !Array.isArray(body) ? body : null;
}

function restProbeConfirms(result) {
  const body = okBody(result);
  return Boolean(body) && body.mongoBackend === true;
}

function graphqlProbeConfirms(result) {
  const body = okBody(result);
  if (!body || body.errors) return false;
  const data = body.data;
  return Boolean(data) && typeof data === 'object'
    && typeof data.__typename === 'string' && data.__typename !== '';
}

function probeUrl() {
  const rest = getMongoBackendUrls().rest.replace(/\/+$/, '');
  return `${rest}${PROBE_PATH}`;
}

function readProbeResponse(response) {
  return Promise.resolve()
    .then(() => response.json())
    .then((body) => ({ status: response.status, body }))
    .catch(() => ({ status: response.status, body: null }));
}

// Default probe: a bare fetch with the Bearer token. Injectable for tests.
// `redirect: 'error'`: a redirect would re-send the Authorization header to
// whatever host the redirect names; refusing it maps to reason 'network'.
export function defaultProbe({ url, token, signal }) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  // Always a promise, even when `fetch` is missing (old WebView): the
  // resolver maps a rejection to reason 'network'.
  return Promise.resolve()
    .then(() => fetch(url, { method: 'GET', headers, signal, redirect: 'error' }))
    .then(readProbeResponse);
}

// Default GraphQL probe: the cheapest valid query there is. It needs no
// introspection and touches no resolver, so it costs the backend nothing.
export function defaultGraphqlProbe({ url, token, signal }) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return Promise.resolve()
    .then(() => fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query: GRAPHQL_PROBE_QUERY }),
      signal,
      redirect: 'error',
    }))
    .then(readProbeResponse);
}

function defaultFetchFlags() {
  return fetchFeatureFlags({ force: true });
}

function safeSentry(fn) {
  try {
    fn();
  } catch (e) {
    // Telemetry must never decide the target.
  }
}

function reportProbeFailure(reason, probe) {
  safeSentry(() => Sentry.captureMessage('backend_target.probe_failed', {
    level: 'warning',
    tags: { backend_target: BACKEND_TARGET_MONGO, reason, probe },
  }));
}

function reportResolved(target, durationMs) {
  safeSentry(() => Sentry.setTag('backend_target', target));
  safeSentry(() => Sentry.addBreadcrumb({
    category: 'backend_target',
    message: 'backend_target.resolved',
    level: 'info',
    data: { target, durationMs },
  }));
}

async function flagSaysMongo(fetchFlags, timeoutMs) {
  try {
    const flags = await withTimeout(() => fetchFlags({ force: true }), timeoutMs);
    return Boolean(flags) && typeof flags === 'object' && !Array.isArray(flags)
      && flags.mongoBackend === true;
  } catch (e) {
    return false;
  }
}

// Runs ONE probe against its absolute URL. Resolves true only on a confirmed
// healthy answer; every other outcome is reported and resolves false.
async function runProbe({ name, probe, url, confirms }, token, timeoutMs) {
  let result;
  try {
    result = await withTimeout(({ signal }) => probe({ url, token, signal, timeoutMs }), timeoutMs);
  } catch (error) {
    reportProbeFailure(probeFailureReason(error), name);
    return false;
  }
  if (confirms(result)) return true;
  reportProbeFailure(probeFailureReason(null, result), name);
  return false;
}

async function probesConfirmMongo({ probe, graphqlProbe }, timeoutMs) {
  if (timeoutMs <= 0) {
    // The flag fetch consumed the whole budget: do not start probes that
    // would be aborted immediately.
    reportProbeFailure('timeout', PROBE_SKIPPED);
    return false;
  }
  const token = readToken();
  const probes = [
    { name: PROBE_REST, probe, url: probeUrl(), confirms: restProbeConfirms },
    { name: PROBE_GRAPHQL, probe: graphqlProbe, url: getMongoBackendUrls().graphql, confirms: graphqlProbeConfirms },
  ];
  const results = await Promise.all(probes.map((spec) => runProbe(spec, token, timeoutMs)));
  return results.every(Boolean);
}

function normalizeOptions(options) {
  const opts = options && typeof options === 'object' && !Array.isArray(options) ? options : {};
  return {
    fetchFlags: typeof opts.fetchFlags === 'function' ? opts.fetchFlags : defaultFetchFlags,
    probe: typeof opts.probe === 'function' ? opts.probe : defaultProbe,
    graphqlProbe: typeof opts.graphqlProbe === 'function' ? opts.graphqlProbe : defaultGraphqlProbe,
    timeoutMs: normalizeTimeout(opts.timeoutMs),
    now: typeof opts.now === 'function' ? opts.now : Date.now,
  };
}

async function decideTarget({ fetchFlags, probe, graphqlProbe, timeoutMs, now }) {
  if (isBackendOverrideDisabled() || !isMongoBackendConfigured()) return BACKEND_TARGET_ENV;
  const deadlineAt = safeNow(now) + timeoutMs;
  if (!(await flagSaysMongo(fetchFlags, timeoutMs))) return BACKEND_TARGET_ENV;
  const remainingMs = deadlineAt - safeNow(now);
  if (!(await probesConfirmMongo({ probe, graphqlProbe }, remainingMs))) return BACKEND_TARGET_ENV;
  return BACKEND_TARGET_MONGO;
}

export async function resolveBackendTarget(options) {
  const opts = normalizeOptions(options);
  const startedAt = safeNow(opts.now);
  let target = BACKEND_TARGET_ENV;
  try {
    target = await decideTarget(opts);
  } catch (e) {
    target = BACKEND_TARGET_ENV;
  }
  reportResolved(target, safeNow(opts.now) - startedAt);
  return target;
}
